/**
 * Pure preprocessor functions for custom OpenSCAD modifier syntax.
 *
 * These are extracted from the OpenSCAD worker class so they can be reused
 * in other contexts (e.g. the real-time syntax checker) without pulling in
 * the WASM-heavy worker module.
 */
import { CodeFile, ParsingHelper } from 'openscad-parser';
import { jsonToScadFunction, filenameToScadName } from './json-to-scad';
import { urlToFilename } from './api-include-fetcher';
import type { InteractiveConfig } from '@shared/types';
import type { MotionDef, LightLFO, LFOShape } from '@/worker/types';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Find the end of an OpenSCAD statement starting at position i.
 * Handles nested parentheses and braces.
 */
export function findStatementEnd(code: string, i: number): number {
  let parenDepth = 0;
  let braceDepth = 0;
  let inString = false;
  let stringChar = '';

  while (i < code.length) {
    const char = code[i];
    const prevChar = i > 0 ? code[i - 1] : '';

    // Handle strings
    if ((char === '"' || char === "'") && prevChar !== '\\') {
      if (!inString) {
        inString = true;
        stringChar = char;
      } else if (char === stringChar) {
        inString = false;
      }
      i++;
      continue;
    }

    if (inString) {
      i++;
      continue;
    }

    // Track nesting
    if (char === '(') parenDepth++;
    else if (char === ')') parenDepth--;
    else if (char === '{') braceDepth++;
    else if (char === '}') braceDepth--;

    // Statement ends at ; when not nested, or at } when braces close
    if (parenDepth === 0 && braceDepth === 0 && char === ';') {
      return i + 1;
    }
    if (parenDepth === 0 && braceDepth === 0 && char === '}') {
      return i + 1;
    }
    if (braceDepth < 0) {
      return i;
    }

    i++;
  }

  return i;
}

// ---------------------------------------------------------------------------
// % ghost modifier
// ---------------------------------------------------------------------------

/**
 * Check if % at position i is a ghost modifier (not part of a modulo operation).
 */
export function isGhostModifier(code: string, i: number): boolean {
  // Look backwards to see if this could be a modulo operator
  let j = i - 1;
  while (j >= 0 && /\s/.test(code[j])) j--;

  if (j >= 0) {
    const prevChar = code[j];
    // If preceded by a number, identifier char, or ), it's likely modulo
    if (/[\d\w\)]/.test(prevChar)) {
      return false;
    }
  }

  // Look forward - should be followed by a valid statement start
  let k = i + 1;
  while (k < code.length && /\s/.test(code[k])) k++;

  if (k < code.length) {
    // Should start with identifier, { or another modifier
    return /[a-zA-Z_{%#!*~]/.test(code[k]);
  }

  return false;
}

/**
 * Transforms: %cube([10,10,10]); -> __ghost_wrapper__() { cube([10,10,10]); }
 */
export function preprocessGhostModifiers(code: string): string {
  let result = '';
  let i = 0;

  while (i < code.length) {
    if (code[i] === '%' && isGhostModifier(code, i)) {
      let j = i + 1;
      while (j < code.length && /\s/.test(code[j])) j++;

      const statementEnd = findStatementEnd(code, j);
      if (statementEnd > j) {
        const statement = code.slice(j, statementEnd);
        result += `__ghost_wrapper__() { ${statement} }`;
        i = statementEnd;
        continue;
      }
    }

    result += code[i];
    i++;
  }

  return result;
}

// ---------------------------------------------------------------------------
// ~ exclude-from-export modifier
// ---------------------------------------------------------------------------

/**
 * Check if ~ at position i is an exclude-from-export modifier.
 * Since ~ is not a valid OpenSCAD operator, the only false-positive
 * contexts are inside string literals or line/block comments.
 */
function isTildeModifier(code: string, i: number): boolean {
  // Walk forward from the start to check we're not inside a string or comment
  let inString = false;
  let stringChar = '';
  let inLineComment = false;
  let inBlockComment = false;

  for (let k = 0; k < i; k++) {
    const c = code[k];
    const next = code[k + 1];

    if (inLineComment) {
      if (c === '\n') inLineComment = false;
      continue;
    }
    if (inBlockComment) {
      if (c === '*' && next === '/') {
        inBlockComment = false;
        k++; // skip the '/'
      }
      continue;
    }
    if (inString) {
      if (c === '\\') {
        k++; // skip escaped char
        continue;
      }
      if (c === stringChar) inString = false;
      continue;
    }
    // Not in any special context
    if (c === '/' && next === '/') {
      inLineComment = true;
      k++;
      continue;
    }
    if (c === '/' && next === '*') {
      inBlockComment = true;
      k++;
      continue;
    }
    if (c === '"' || c === "'") {
      inString = true;
      stringChar = c;
      continue;
    }
  }

  // If we ended up inside a string or comment, this ~ is not a modifier
  if (inString || inLineComment || inBlockComment) return false;

  // Look forward — should be followed by a valid statement start
  let k = i + 1;
  while (k < code.length && /\s/.test(code[k])) k++;

  if (k < code.length) {
    return /[a-zA-Z_{%#!*~]/.test(code[k]);
  }

  return false;
}

/**
 * Transforms: ~cube([10,10,10]); -> exclude_from_export() { cube([10,10,10]); }
 */
export function preprocessExcludeModifiers(code: string): string {
  let result = '';
  let i = 0;

  while (i < code.length) {
    if (code[i] === '~' && isTildeModifier(code, i)) {
      let j = i + 1;
      while (j < code.length && /\s/.test(code[j])) j++;

      const statementEnd = findStatementEnd(code, j);
      if (statementEnd > j) {
        const statement = code.slice(j, statementEnd);
        result += `exclude_from_export() { ${statement} }`;
        i = statementEnd;
        continue;
      }
    }

    result += code[i];
    i++;
  }

  return result;
}

// ---------------------------------------------------------------------------
// JSON include preprocessing
// ---------------------------------------------------------------------------

/**
 * A minimal view of an uploaded project file — only the fields needed by the
 * JSON preprocessor.  Using a narrow interface avoids a hard dependency on the
 * `ProjectFile` type defined in `FileManager.tsx`.
 */
export interface JsonProjectFile {
  filename: string;
  content: string | ArrayBuffer;
}

/**
 * Scans the SCAD source for `include <name.json>` lines and replaces each one
 * with an inlined OpenSCAD function generated from the corresponding JSON file.
 *
 * Lines that reference a `.json` file not present in `projectFiles` are left
 * untouched — the WASM compiler will then produce its normal "file not found"
 * error, consistent with missing `.scad` includes.
 *
 * @param code         Raw SCAD source code.
 * @param projectFiles Uploaded project files available in the current session.
 * @returns            SCAD source with JSON includes replaced by function defs.
 */
/**
 * Recognises both file-based and URL-based JSON includes.
 *
 *   include <data.json>                         → group 1 = "data.json"
 *   include <https://example.com/api/scores>    → group 1 = full URL
 *   include <http://localhost:3000/data.json>   → group 1 = full URL
 */
const JSON_INCLUDE_RE = /^include\s*<(https?:\/\/[^>]+|[^>]+\.json)>/i;

/**
 * Given the matched path/URL from a JSON include directive, return the
 * filename used to look up the entry in the file map.
 */
function resolveIncludeFilename(matchedPath: string): string {
  if (/^https?:\/\//i.test(matchedPath)) {
    // URL-form include: key is the last path segment derived by the fetcher.
    return urlToFilename(matchedPath);
  }
  // File-form include: key is the basename.
  return matchedPath.split('/').pop() ?? matchedPath;
}

export function preprocessJsonIncludes(
  code: string,
  projectFiles: JsonProjectFile[],
): string {
  if (projectFiles.length === 0) return code;

  // Build a fast lookup: lowercase filename → file object.
  const fileMap = new Map<string, JsonProjectFile>();
  for (const f of projectFiles) {
    fileMap.set(f.filename.toLowerCase(), f);
  }

  const lines = code.split('\n');
  const result: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Match: include <something.json>  OR  include <https://...>
    const m = trimmed.match(JSON_INCLUDE_RE);
    if (m) {
      const matchedPath = m[1];
      const referencedFilename = resolveIncludeFilename(matchedPath);
      const file = fileMap.get(referencedFilename.toLowerCase());

      if (file) {
        // Parse the JSON content.
        let rawText: string;
        if (typeof file.content === 'string') {
          rawText = file.content;
        } else {
          // ArrayBuffer — decode as UTF-8.
          rawText = new TextDecoder().decode(file.content);
        }

        let parsed: unknown;
        try {
          parsed = JSON.parse(rawText);
        } catch (err) {
          // Emit a SCAD comment so the error surfaces in the OpenSCAD output.
          result.push(
            `// JSON parse error in ${referencedFilename}: ${String(err)}`,
          );
          continue;
        }

        const fnName = filenameToScadName(referencedFilename);
        const { scad } = jsonToScadFunction(fnName, parsed);

        // Replace the include line with the generated SCAD block.
        result.push(scad);
        continue;
      }
      // File/URL not resolved — leave the line as-is so the WASM reports the error.
    }

    result.push(line);
  }

  return result.join('\n');
}

// ---------------------------------------------------------------------------
// interactive() module preprocessing
// ---------------------------------------------------------------------------

/**
 * Result of preprocessing interactive() calls.
 * The transformed source has each `interactive(...)` replaced by
 * `__interactive__(id, ...)` where id is a stable 0-based index.
 * The map lets the worker associate the echo'd id with the original config.
 */
export interface InteractivePreprocessResult {
  code: string;
  /** Map from numeric id (as string key) to the InteractiveConfig for that region. */
  interactiveMap: Map<number, InteractiveConfig>;
}

/**
 * Scan for `interactive(...)` and `interactive_publish(...)` calls and rewrite
 * each to a `__interactive__(id, ...)` or `__interactive_publish__(id, ...)` call
 * where id is a unique 0-based integer index.  Returns both the rewritten code
 * and a map from id → InteractiveConfig used by the worker to associate echo
 * output with the correct config.
 *
 * Supported forms for interactive():
 *   interactive("var")                           → boolean toggle on click
 *   interactive("var", value=X)                  → set to X on click
 *   interactive("var", ["a","b"])                → cycle through options on click
 *   interactive("var", key="pub_key")            → toggle + publish to pub/sub key
 *   interactive("var", value=X, key="pub_key")   → set + publish
 *   interactive("var", ["a","b"], key="pub_key") → cycle + publish
 *
 * Supported forms for interactive_publish():
 *   interactive_publish("pub_key")               → publish undef on click (no value)
 *   interactive_publish("pub_key", value=X)      → always publish X on click
 *   interactive_publish("pub_key", ["a","b"])    → cycle + publish
 *
 * The rewrite is intentionally minimal: it only rewrites the module name and
 * prepends the numeric id argument — the rest of the argument list is preserved
 * verbatim so that quoted string variable names and optional arguments are
 * forwarded unchanged.
 *
 * In `stripMeshpitCode()` (for desktop OpenSCAD export), both `interactive` and
 * `interactive_publish` are treated as wrapper modules: the call is removed and
 * the children are kept.
 */
export function preprocessInteractiveModules(
  code: string,
): InteractivePreprocessResult {
  const interactiveMap = new Map<number, InteractiveConfig>();
  let idCounter = 0;

  // Match both `interactive` and `interactive_publish` as whole words followed
  // by an opening parenthesis.  The longer name must be checked first so the
  // regex doesn't match `interactive` as a prefix of `interactive_publish`.
  const re = /(?<![a-zA-Z_$])(interactive_publish|interactive)\s*\(/g;

  // Collect all matches first (end → start order for safe splice)
  const matches: {
    start: number;
    parenStart: number;
    isPublishOnly: boolean;
  }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(code)) !== null) {
    matches.push({
      start: m.index,
      parenStart: m.index + m[0].length - 1,
      isPublishOnly: m[1] === 'interactive_publish',
    });
  }

  if (matches.length === 0) {
    return { code, interactiveMap };
  }

  // Process from last to first so indices don't shift.
  let result = code;
  for (let k = matches.length - 1; k >= 0; k--) {
    const { start, parenStart, isPublishOnly } = matches[k];

    // Find the closing paren of the argument list.
    const closeParen = findClosingParen(result, parenStart);
    if (closeParen === -1) continue;

    // Extract the raw argument string (between the outer parens).
    const rawArgs = result.slice(parenStart + 1, closeParen - 1).trim();

    const config = isPublishOnly
      ? parseInteractivePublishArgs(rawArgs)
      : parseInteractiveArgs(rawArgs);
    if (!config) continue;

    const id = idCounter++;
    interactiveMap.set(id, config);

    const internalName = isPublishOnly
      ? '__interactive_publish__'
      : '__interactive__';

    // Rewrite: interactive[_publish](...) → __interactive[_publish]__(id, ...)
    const newCall =
      `${internalName}(${id}, ` +
      result.slice(parenStart + 1, closeParen - 1) +
      ')';
    result = result.slice(0, start) + newCall + result.slice(closeParen);
  }

  return { code: result, interactiveMap };
}

/**
 * Parse a scalar value expression (used by both interactive() and
 * interactive_publish() argument parsers).
 * Handles: true, false, <number>, "string".
 */
function parseScalarValue(raw: string): string | number | boolean | undefined {
  const trimmed = raw.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return parseFloat(trimmed);
  const strMatch = trimmed.match(/^"([^"]*)"$/);
  if (strMatch) return strMatch[1];
  return trimmed || undefined; // fallback
}

/**
 * Extract the value of a named argument `key="..."` from a raw argument string,
 * returning the string value or undefined if not present.
 */
function extractKeyArg(raw: string): string | undefined {
  const m = raw.match(/(?:^|,)\s*key\s*=\s*"([^"]+)"/);
  return m ? m[1] : undefined;
}

/**
 * Remove a named `key="..."` argument from a raw argument string so the
 * remaining args can be parsed normally by the SCAD echo module.
 */
function stripKeyArg(raw: string): string {
  return raw.replace(/,\s*key\s*=\s*"[^"]*"/, '').trim();
}

/**
 * Parse the raw argument string of an `interactive(...)` call.
 * Expected forms:
 *   "variable_name"
 *   "variable_name", value=true
 *   "variable_name", value=42
 *   "variable_name", value="someString"
 *   "variable_name", ["opt1", "opt2", "opt3"]   ← cycle through strings
 *   "variable_name", [2, 4, 8, 16]              ← cycle through numbers
 *   any of the above with an additional , key="pub_key" argument
 *
 * Returns null if the first argument is not a quoted string (i.e. this is not
 * a valid interactive() call and should be left untouched).
 */
function parseInteractiveArgs(raw: string): InteractiveConfig | null {
  // First argument must be a quoted string.
  const firstArgMatch = raw.match(/^"([^"]+)"/);
  if (!firstArgMatch) return null;

  const variable = firstArgMatch[1];

  // Extract and strip the optional key= argument before further parsing
  const publishKey = extractKeyArg(raw);
  const rawWithoutKey = stripKeyArg(raw);

  // Re-match on the stripped string
  const firstArgStripped = rawWithoutKey.match(/^"([^"]+)"/);
  if (!firstArgStripped) return null;

  // Rest of the argument string after the variable name (key already stripped).
  const afterVar = rawWithoutKey.slice(firstArgStripped[0].length).trimStart();

  // Check for cycle-through-options syntax: , [...]
  if (afterVar.startsWith(',')) {
    const afterComma = afterVar.slice(1).trimStart();

    if (afterComma.startsWith('[')) {
      // Parse array literal — collect everything up to the matching ']'.
      const options = parseScadArrayLiteral(afterComma);
      if (options !== null) {
        return { variable, options, ...(publishKey ? { publishKey } : {}) };
      }
    } else {
      // Look for an optional `value=<expr>` argument.
      const valueMatch = afterComma.match(/^value\s*=\s*(.+)$/s);
      if (valueMatch) {
        const value = parseScalarValue(valueMatch[1]);
        return {
          variable,
          ...(value !== undefined ? { value } : {}),
          ...(publishKey ? { publishKey } : {}),
        };
      }
    }
  }

  return { variable, ...(publishKey ? { publishKey } : {}) };
}

/**
 * Parse the raw argument string of an `interactive_publish(...)` call.
 * Expected forms:
 *   "pub_key"                   → publishes undefined on click (can be used as a signal)
 *   "pub_key", value=X          → always publishes X on click
 *   "pub_key", ["a","b","c"]    → cycles through options and publishes the new value
 *
 * Returns null if the first argument is not a quoted string.
 */
function parseInteractivePublishArgs(raw: string): InteractiveConfig | null {
  const firstArgMatch = raw.match(/^"([^"]+)"/);
  if (!firstArgMatch) return null;

  const publishKey = firstArgMatch[1];
  const afterKey = raw.slice(firstArgMatch[0].length).trimStart();

  if (afterKey.startsWith(',')) {
    const afterComma = afterKey.slice(1).trimStart();

    if (afterComma.startsWith('[')) {
      const options = parseScadArrayLiteral(afterComma);
      if (options !== null) {
        return { variable: '', publishKey, publishOnly: true, options };
      }
    } else {
      const valueMatch = afterComma.match(/^value\s*=\s*(.+)$/s);
      if (valueMatch) {
        const value = parseScalarValue(valueMatch[1]);
        return {
          variable: '',
          publishKey,
          publishOnly: true,
          ...(value !== undefined ? { value } : {}),
        };
      }
    }
  }

  return { variable: '', publishKey, publishOnly: true };
}

/**
 * Parse a SCAD array literal of the form `[item, item, ...]` where each item
 * is either a quoted string or a number (integers and decimals, optionally
 * negative). Returns a typed array (string[] or number[]), or null if the
 * literal cannot be parsed.
 *
 * Mixed-type arrays (strings and numbers) are not supported and return null.
 */
function parseScadArrayLiteral(raw: string): string[] | number[] | null {
  // Find the matching closing bracket.
  let depth = 0;
  let end = -1;
  let inStr = false;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === '"' && (i === 0 || raw[i - 1] !== '\\')) {
      inStr = !inStr;
      continue;
    }
    if (inStr) continue;
    if (ch === '[') depth++;
    else if (ch === ']') {
      depth--;
      if (depth === 0) {
        end = i;
        break;
      }
    }
  }
  if (end === -1) return null;

  const inner = raw.slice(1, end).trim();
  if (!inner) return []; // empty array

  // Split on commas that are not inside nested brackets or strings.
  const items = splitScadArgs(inner);

  const strings: string[] = [];
  const numbers: number[] = [];

  for (const item of items) {
    const t = item.trim();
    const strMatch = t.match(/^"([^"]*)"$/);
    if (strMatch) {
      strings.push(strMatch[1]);
      continue;
    }
    if (/^-?\d+(\.\d+)?$/.test(t)) {
      numbers.push(parseFloat(t));
      continue;
    }
    // Unrecognised token — bail out.
    return null;
  }

  if (strings.length > 0 && numbers.length > 0) {
    // Mixed types — not supported.
    return null;
  }
  if (strings.length > 0) return strings;
  if (numbers.length > 0) return numbers;
  return [];
}

/**
 * Split a comma-separated SCAD argument string, respecting nested brackets
 * and quoted strings. Returns an array of raw token strings.
 */
function splitScadArgs(raw: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let inStr = false;
  let start = 0;

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === '"' && (i === 0 || raw[i - 1] !== '\\')) {
      inStr = !inStr;
      continue;
    }
    if (inStr) continue;
    if (ch === '[' || ch === '(') depth++;
    else if (ch === ']' || ch === ')') depth--;
    else if (ch === ',' && depth === 0) {
      parts.push(raw.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(raw.slice(start));
  return parts;
}

/**
 * Apply all custom modifier preprocessing in the correct order.
 * Use this as a single entry point for both WASM compilation and syntax checking.
 *
 * @param code         Raw SCAD source.
 * @param projectFiles Optional list of uploaded project files; required for JSON
 *                     include resolution.  When omitted (or empty) the JSON step
 *                     is skipped, preserving backwards compatibility for callers
 *                     that don't have access to project files (e.g. unit tests).
 */
export function preprocessScadModifiers(
  code: string,
  projectFiles?: JsonProjectFile[],
): string {
  // Strip @pubsub parameter declarations first so downstream passes never
  // see them and the WASM compiler is never handed these variables.
  let processed = stripPubSubDeclarations(code);
  processed = preprocessGhostModifiers(processed);
  processed = preprocessExcludeModifiers(processed);
  // Note: interactive() is intentionally NOT preprocessed here.
  // The interactive() → __interactive__(id, ...) rewrite must happen inside
  // the worker's detectColors() so that the id→config map is available when
  // parsing the echo output.  Rewriting on the main thread would consume the
  // interactive() calls before they reach the worker, leaving _lastInteractiveMap
  // empty and breaking click detection.
  // The worker's generateColorSTL preamble defines interactive() and
  // __interactive__() as no-op stubs, so either form is safe there.
  if (projectFiles && projectFiles.length > 0) {
    processed = preprocessJsonIncludes(processed, projectFiles);
  }
  return processed;
}

// ---------------------------------------------------------------------------
// motion() module preprocessing
// ---------------------------------------------------------------------------

/**
 * Result of preprocessing motion() calls.
 * The transformed source has each `motion(...)` replaced by
 * `__motion__(id, ...)` where id is a stable 0-based index.
 * The map lets the worker associate the echo'd id with the original MotionDef.
 */
export interface MotionPreprocessResult {
  code: string;
  /** Map from numeric id → MotionDef for that wrapped geometry region. */
  motionMap: Map<number, MotionDef>;
}

/** Valid LFO shape names for validation */
const VALID_MOTION_LFO_SHAPES: LFOShape[] = [
  'sine',
  'triangle',
  'square',
  'sawtooth',
];

/**
 * Parse an inline channel-LFO string from a motion() call argument.
 * Format: "shape:freq:phase:min:max"
 * Returns undefined if empty or malformed.
 */
function parseMotionLFO(raw: string | undefined): LightLFO | undefined {
  if (!raw) return undefined;
  const s = raw.trim();
  if (!s) return undefined;
  const parts = s.split(':');
  if (parts.length !== 5) return undefined;
  const shape = parts[0].trim();
  if (!(VALID_MOTION_LFO_SHAPES as string[]).includes(shape)) return undefined;
  const frequency = parseFloat(parts[1]);
  const phase = parseFloat(parts[2]);
  const min = parseFloat(parts[3]);
  const max = parseFloat(parts[4]);
  if ([frequency, phase, min, max].some(isNaN)) return undefined;
  return { shape: shape as LFOShape, frequency, phase, min, max };
}

/**
 * Parse a SCAD vector literal [a, b, c] from a raw string.
 * Returns the default if not parseable.
 */
function parseMotionVec3(
  raw: string | undefined,
  defaultVec: [number, number, number],
): [number, number, number] {
  if (!raw) return defaultVec;
  const m = raw
    .trim()
    .match(/^\[\s*([-\d.e+]+)\s*,\s*([-\d.e+]+)\s*,\s*([-\d.e+]+)\s*\]$/);
  if (!m) return defaultVec;
  return [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3])];
}

/**
 * Parse the raw argument string of a motion() call into a MotionDef.
 * Handles named parameters only (positional is fragile for a large param set).
 *
 * Parameter order (for positional fallback):
 *   0: translate_axis, 1: translate_lfo,
 *   2: orbit_center, 3: orbit_axis, 4: orbit_radius, 5: orbit_radius_b,
 *   6: orbit_speed, 7: orbit_offset,
 *   8: rotate_axis, 9: rotate_speed, 10: rotate_offset,
 *   11: rock_axis, 12: rock_lfo
 */
function parseMotionArgs(rawArgs: string): MotionDef | null {
  // Build named param map
  const byName = new Map<string, string>();
  const positional: string[] = [];

  // Reuse the same splitArgs logic inline (avoid circular dep on scad-scene-parser)
  const parts: string[] = [];
  let depth = 0;
  let inStr = false;
  let start = 0;
  for (let i = 0; i < rawArgs.length; i++) {
    const ch = rawArgs[i];
    if (ch === '"' && (i === 0 || rawArgs[i - 1] !== '\\')) {
      inStr = !inStr;
      continue;
    }
    if (inStr) continue;
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth--;
    else if (ch === ',' && depth === 0) {
      parts.push(rawArgs.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(rawArgs.slice(start));

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx !== -1) {
      const before = trimmed.slice(0, eqIdx);
      if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(before.trim())) {
        byName.set(before.trim(), trimmed.slice(eqIdx + 1).trim());
        positional.push(trimmed.slice(eqIdx + 1).trim());
        continue;
      }
    }
    positional.push(trimmed);
  }

  const getStr = (name: string, idx: number): string | undefined => {
    const raw = byName.get(name) ?? positional[idx];
    if (raw === undefined) return undefined;
    const t = raw.trim();
    if (t.startsWith('"') && t.endsWith('"')) return t.slice(1, -1);
    return t;
  };
  const getNum = (name: string, idx: number, def: number): number => {
    const raw = byName.get(name) ?? positional[idx];
    if (raw === undefined) return def;
    const v = parseFloat(raw);
    return isNaN(v) ? def : v;
  };
  const getVec = (
    name: string,
    idx: number,
    def: [number, number, number],
  ): [number, number, number] =>
    parseMotionVec3(byName.get(name) ?? positional[idx], def);

  const translateAxis = getVec('translate_axis', 0, [0, 1, 0]);
  const translateLFO = parseMotionLFO(getStr('translate_lfo', 1));

  const orbitCenter = getVec('orbit_center', 2, [0, 0, 0]);
  const orbitAxis = getVec('orbit_axis', 3, [0, 0, 1]);
  const orbitRadius = getNum('orbit_radius', 4, 0);
  const orbitRadiusB = getNum('orbit_radius_b', 5, 0);
  const orbitSpeed = getNum('orbit_speed', 6, 0.5);
  const orbitOffset = getNum('orbit_offset', 7, 0);

  const rotateAxis = getVec('rotate_axis', 8, [0, 0, 1]);
  const rotateSpeed = getNum('rotate_speed', 9, 0);
  const rotateOffset = getNum('rotate_offset', 10, 0);

  const rockAxis = getVec('rock_axis', 11, [1, 0, 0]);
  const rockLFO = parseMotionLFO(getStr('rock_lfo', 12));

  return {
    translateAxis,
    ...(translateLFO && { translateLFO }),
    orbitCenter,
    orbitAxis,
    orbitRadius,
    orbitRadiusB,
    orbitSpeed,
    orbitOffset,
    rotateAxis,
    rotateSpeed,
    rotateOffset,
    rockAxis,
    ...(rockLFO && { rockLFO }),
  };
}

/**
 * Scan for `motion(...)` wrapper calls and rewrite each to
 * `__motion__(id, ...)` where id is a unique 0-based integer index.
 *
 * Returns the rewritten code and a map from id → MotionDef used by the worker
 * to associate MOTION_FOUND echo output with the correct animation definition.
 *
 * In `stripMeshpitCode()` (for desktop OpenSCAD export), `motion` is treated
 * as a wrapper module: the call is removed and the children are kept, so the
 * geometry renders statically without animation.
 */
export function preprocessMotionModules(code: string): MotionPreprocessResult {
  const motionMap = new Map<number, MotionDef>();
  let idCounter = 0;

  const re = /(?<![a-zA-Z_$])(motion)\s*\(/g;

  const matches: { start: number; parenStart: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(code)) !== null) {
    matches.push({
      start: m.index,
      parenStart: m.index + m[0].length - 1,
    });
  }

  if (matches.length === 0) {
    return { code, motionMap };
  }

  // Process from last to first so indices don't shift.
  let result = code;
  for (let k = matches.length - 1; k >= 0; k--) {
    const { start, parenStart } = matches[k];

    const closeParen = findClosingParen(result, parenStart);
    if (closeParen === -1) continue;

    const rawArgs = result.slice(parenStart + 1, closeParen - 1).trim();

    const motionDef = parseMotionArgs(rawArgs);
    if (!motionDef) continue;

    const id = idCounter++;
    motionMap.set(id, motionDef);

    // Rewrite: motion(...) → __motion__(id, ...)
    const newCall =
      `__motion__(${id}, ` + result.slice(parenStart + 1, closeParen - 1) + ')';
    result = result.slice(0, start) + newCall + result.slice(closeParen);
  }

  return { code: result, motionMap };
}

// ---------------------------------------------------------------------------
// Strip Meshpit-specific code for pure OpenSCAD export
// ---------------------------------------------------------------------------

/**
 * Meshpit modules that wrap children — the module call (and its argument list)
 * are removed but the child block is kept.
 */
const MESHPIT_WRAPPER_MODULES = [
  'material',
  'texture_script',
  'texture_svg',
  'texture_png',
  'interactive',
  'interactive_publish',
  'annotation',
  'motion',
  'clone_ref',
  'shader_material',
];

/**
 * Meshpit modules that are standalone statements (no meaningful children).
 * The entire invocation (including any trailing semicolon) is removed.
 */
const MESHPIT_STANDALONE_MODULES = [
  'light_ambient',
  'light_directional',
  'light_point',
  'light_spot',
  'scene_material',
  'scene_default_lights',
  'scene_shadows',
  'scene_bloom',
  'scene_ssao',
  'scene_background',
  'scene_grid',
  'camera_pip',
  'exclude_from_export',
  'clone',
  // js_svg() produces no children — the entire call is stripped for desktop export.
  // (In the playground it is replaced by import() calls before compilation.)
  'js_svg',
];

/**
 * Strip all Meshpit-specific extensions from OpenSCAD source code so that the
 * result can be opened in the standard OpenSCAD desktop application.
 *
 * What gets removed / transformed:
 * - `~` modifier prefix → removed (keeps the statement)
 * - `material(...)` / `texture_script(...)` / `shader_material(...)` wrappers → removed, children kept
 * - `material_def(...)` function calls → removed (whole assignment line or inline)
 * - All light_* modules → removed entirely
 * - All scene_* modules → removed entirely
 * - `camera_pip(...)` → removed entirely
 * - `exclude_from_export()` → removed, children kept
 *
 * The function operates line-by-line for simple cases and falls back to
 * character-level scanning for multi-line constructs.
 */
export function stripMeshpitCode(code: string): string {
  // Pass 1: Remove ~ modifiers (replace with space to preserve alignment/offsets)
  let result = '';
  for (let i = 0; i < code.length; i++) {
    if (code[i] === '~' && isTildeModifier(code, i)) {
      // Skip the ~ but keep everything after it
      result += ' ';
    } else {
      result += code[i];
    }
  }

  // Pass 2: Remove standalone Meshpit modules (entire statement)
  for (const mod of MESHPIT_STANDALONE_MODULES) {
    result = stripStandaloneModule(result, mod);
  }

  // Pass 3: Remove wrapper modules (keep children)
  for (const mod of MESHPIT_WRAPPER_MODULES) {
    result = stripWrapperModule(result, mod);
  }

  // Pass 4: Remove material_def variable assignments and inline calls
  result = stripMaterialDef(result);

  // Pass 5: Clean up empty lines (collapse runs of 3+ blank lines into 2)
  result = result.replace(/(\n\s*){3,}\n/g, '\n\n');

  return result.trimEnd() + '\n';
}

/**
 * Build a regex that matches a module/function name as a whole word,
 * not preceded by an identifier character (to avoid matching e.g. `my_material`).
 */
function moduleCallRegex(name: string): RegExp {
  return new RegExp(`(?<![a-zA-Z_$])${name}\\s*\\(`, 'g');
}

/**
 * Find the end of a balanced parenthesised argument list starting at the
 * opening '(' at position `openParen`.  Returns the index *after* the closing ')'.
 * Returns -1 if unbalanced.
 */
function findClosingParen(code: string, openParen: number): number {
  let depth = 0;
  let inStr = false;
  for (let i = openParen; i < code.length; i++) {
    const ch = code[i];
    if (ch === '"' && (i === 0 || code[i - 1] !== '\\')) {
      inStr = !inStr;
      continue;
    }
    if (inStr) continue;
    if (ch === '(') depth++;
    else if (ch === ')') {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}

/**
 * Remove a standalone module call (one that produces no children geometry).
 * Handles both `module_name(...);` and `module_name(...) { ... }`.
 * If preceded only by whitespace on its line, the entire line is removed.
 */
function stripStandaloneModule(code: string, name: string): string {
  const re = moduleCallRegex(name);
  // Work from the end so indices don't shift
  const matches: { start: number; parenStart: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(code)) !== null) {
    matches.push({
      start: m.index,
      parenStart: m.index + m[0].length - 1, // index of '('
    });
  }

  // Process from last to first so splicing doesn't shift earlier indices
  for (let k = matches.length - 1; k >= 0; k--) {
    const { start, parenStart } = matches[k];
    const closeParen = findClosingParen(code, parenStart);
    if (closeParen === -1) continue;

    // After the closing paren, skip whitespace and look for ; or { ... }
    let end = closeParen;
    while (end < code.length && /\s/.test(code[end])) end++;

    if (end < code.length && code[end] === ';') {
      end++; // consume the semicolon
    } else if (end < code.length && code[end] === '{') {
      // Find matching closing brace
      const braceEnd = findStatementEnd(code, end);
      end = braceEnd;
    }

    // Expand to cover the whole line if it's otherwise empty
    let lineStart = start;
    while (lineStart > 0 && code[lineStart - 1] !== '\n') lineStart--;
    const before = code.slice(lineStart, start);
    let lineEnd = end;
    while (lineEnd < code.length && code[lineEnd] !== '\n') lineEnd++;
    const after = code.slice(end, lineEnd);

    if (before.trim() === '' && after.trim() === '') {
      // Remove the whole line (including trailing newline)
      if (lineEnd < code.length && code[lineEnd] === '\n') lineEnd++;
      code = code.slice(0, lineStart) + code.slice(lineEnd);
    } else {
      code = code.slice(0, start) + code.slice(end);
    }
  }

  return code;
}

/**
 * Remove a wrapper module call but keep its children.
 * `material(material_def(...)) cube(10);` → `cube(10);`
 * `material(mat) { cube(10); sphere(5); }` → `cube(10); sphere(5);`
 */
function stripWrapperModule(code: string, name: string): string {
  const re = moduleCallRegex(name);
  const matches: { start: number; parenStart: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(code)) !== null) {
    matches.push({
      start: m.index,
      parenStart: m.index + m[0].length - 1,
    });
  }

  for (let k = matches.length - 1; k >= 0; k--) {
    const { start, parenStart } = matches[k];
    const closeParen = findClosingParen(code, parenStart);
    if (closeParen === -1) continue;

    // After closing paren, skip whitespace
    let afterArgs = closeParen;
    while (afterArgs < code.length && /[ \t]/.test(code[afterArgs]))
      afterArgs++;

    if (afterArgs < code.length && code[afterArgs] === '{') {
      // Braced children block — extract inner content
      const braceStart = afterArgs;
      const braceEnd = findMatchingBrace(code, braceStart);
      if (braceEnd === -1) continue;

      // Inner content is everything between { and }
      const inner = code.slice(braceStart + 1, braceEnd - 1).trimEnd();

      // Check if the wrapper was the only thing on its line
      let lineStart = start;
      while (lineStart > 0 && code[lineStart - 1] !== '\n') lineStart--;
      const indent = code.slice(lineStart, start);

      if (indent.trim() === '') {
        // Dedent the inner block by one level (2 spaces)
        const dedented = dedentBlock(inner, 2);
        code = code.slice(0, lineStart) + dedented + code.slice(braceEnd);
      } else {
        code = code.slice(0, start) + inner + code.slice(braceEnd);
      }
    } else {
      // Single-statement child (no braces): just remove the wrapper call
      // e.g. `material(mat)\n  cube(10);` → `cube(10);`
      // Remove from start to afterArgs (skip any newline after args)
      let removeEnd = afterArgs;
      if (removeEnd < code.length && code[removeEnd] === '\n') {
        removeEnd++;
      }
      code = code.slice(0, start) + code.slice(removeEnd);
    }
  }

  return code;
}

/**
 * Find the position after a matching closing brace for an opening brace at `pos`.
 * Respects string literals.  Returns -1 if unbalanced.
 */
function findMatchingBrace(code: string, pos: number): number {
  let depth = 0;
  let inStr = false;
  for (let i = pos; i < code.length; i++) {
    const ch = code[i];
    if (ch === '"' && (i === 0 || code[i - 1] !== '\\')) {
      inStr = !inStr;
      continue;
    }
    if (inStr) continue;
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}

/**
 * Remove leading whitespace from each line of `block`, up to `spaces` spaces.
 */
function dedentBlock(block: string, spaces: number): string {
  const lines = block.split('\n');
  const prefix = ' '.repeat(spaces);
  return lines
    .map((line) => {
      if (line.startsWith(prefix)) return line.slice(spaces);
      // If line starts with fewer spaces, strip what we can
      const match = line.match(/^(\s*)/);
      if (match && match[1].length > 0 && match[1].length < spaces) {
        return line.slice(match[1].length);
      }
      return line;
    })
    .join('\n');
}

/**
 * Remove `material_def(...)` calls.
 *
 * Two forms:
 * 1. Variable assignment: `varname = material_def(...);` → remove whole line
 * 2. Inline usage (e.g. as argument): `material_def(...)` → remove the call text
 *    (this mainly catches leftover inline uses after wrapper stripping)
 */
function stripMaterialDef(code: string): string {
  // First: remove whole-line variable assignments like `gold = material_def(...);`
  const assignRe =
    /^[ \t]*[a-zA-Z_$][\w]*\s*=\s*material_def\s*\([^)]*\)\s*;[ \t]*$/gm;
  code = code.replace(assignRe, '');

  // Second: remove any remaining inline material_def(...) calls
  const re = moduleCallRegex('material_def');
  const matches: { start: number; parenStart: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(code)) !== null) {
    matches.push({
      start: m.index,
      parenStart: m.index + m[0].length - 1,
    });
  }

  for (let k = matches.length - 1; k >= 0; k--) {
    const { start, parenStart } = matches[k];
    const closeParen = findClosingParen(code, parenStart);
    if (closeParen === -1) continue;
    code = code.slice(0, start) + code.slice(closeParen);
  }

  return code;
}

// ---------------------------------------------------------------------------
// @constraint annotations
// ---------------------------------------------------------------------------

/**
 * Constraint types that can be declared via `// @constraint <type>`.
 * Extend this union when new constraint types are added.
 *
 * - `build-volume`: model must fit entirely inside the ghost envelope.
 * - `keep-out`:     no model geometry may enter or intersect the ghost region.
 * - `clearance`:    model must stay at least `distance` mm away from the ghost surface.
 */
export type ConstraintType = 'build-volume' | 'keep-out' | 'clearance';

/**
 * A parsed constraint annotation associated with a specific ghost (`%`) object.
 */
export type ConstraintDef = {
  /** The kind of constraint to enforce. */
  type: ConstraintType;
  /** Optional user-visible message shown when the constraint is violated. */
  message?: string;
  /**
   * 0-based index of the ghost (`%`) statement this constraint is attached to.
   * The Nth `%` modifier in the file (counting from 0) corresponds to the Nth
   * ghost blob produced by the compile pipeline.
   */
  ghostIndex: number;
  /**
   * Required minimum separation distance in model units.
   * Only meaningful for the `clearance` constraint type.
   * Specified via `distance=<number>` in the annotation comment.
   */
  distance?: number;
};

/**
 * Parse `// @constraint` annotations from raw SCAD source.
 *
 * Syntax:
 *   // @constraint <type> [distance=<number>] ["optional message"]
 *   %<statement>
 *
 * Examples:
 *   // @constraint build-volume "Build volume exceeded!"
 *   // @constraint keep-out "Mounting boss area — no geometry here"
 *   // @constraint clearance distance=2.5 "Parts must be 2.5 mm apart"
 *
 * The comment must appear on the line **immediately** before the `%` statement
 * (blank lines and other statements between them break the association).
 * Unknown constraint types are silently ignored.
 *
 * @param code Raw SCAD source (before any preprocessing).
 * @returns Array of ConstraintDef values in source order.
 */
export function parseConstraints(code: string): ConstraintDef[] {
  const KNOWN_TYPES: Set<string> = new Set<ConstraintType>([
    'build-volume',
    'keep-out',
    'clearance',
  ]);
  // Captures: [1] type, [2] distance value (optional), [3] message (optional)
  const CONSTRAINT_RE =
    /^\s*\/\/\s*@constraint\s+(\S+)(?:\s+distance=([\d.]+))?(?:\s+"([^"]*)")?/;

  const lines = code.split('\n');
  const results: ConstraintDef[] = [];
  let ghostCounter = 0;
  // Pending annotation from the previous line.
  let pending: Omit<ConstraintDef, 'ghostIndex'> | null = null;

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip blank lines — they break the association between comment and %.
    if (trimmed === '') {
      pending = null;
      continue;
    }

    // Check for a @constraint comment.
    const constraintMatch = trimmed.match(CONSTRAINT_RE);
    if (constraintMatch) {
      const rawType = constraintMatch[1];
      if (KNOWN_TYPES.has(rawType)) {
        const distanceStr = constraintMatch[2];
        pending = {
          type: rawType as ConstraintType,
          distance:
            distanceStr !== undefined ? parseFloat(distanceStr) : undefined,
          message: constraintMatch[3], // undefined when group didn't match
        };
      } else {
        // Unknown type — discard any pending association.
        pending = null;
      }
      continue;
    }

    // Check whether this line starts a ghost (%) statement.
    // Find the first % that is a ghost modifier (not modulo).
    let foundGhost = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '%' && isGhostModifier(line, i)) {
        foundGhost = true;
        break;
      }
    }

    if (foundGhost) {
      if (pending) {
        results.push({ ...pending, ghostIndex: ghostCounter });
        pending = null;
      }
      ghostCounter++;
    } else {
      // Non-blank, non-comment, non-ghost line breaks the association.
      pending = null;
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// @pubsub parameter stripping
// ---------------------------------------------------------------------------

/**
 * Remove variable declaration lines annotated with `@pubsub` from SCAD source.
 *
 * These parameters live only in the pub/sub system — they must NOT be passed
 * to the OpenSCAD compiler (no declaration, no -D flag).  This function strips
 * the raw assignment line so the compiler never sees it.
 *
 * Matches lines of the form:
 *   name = value; // ... @pubsub ...
 * including multi-line assignments that were joined onto one line by
 * joinMultiLineAssignments().  Works on the raw (un-joined) source by matching
 * any line whose trailing comment contains `@pubsub`.
 *
 * The line is replaced with an empty string (preserving surrounding newlines so
 * line numbers for other parameters are unaffected).
 */
export function stripPubSubDeclarations(code: string): string {
  // Match a complete variable assignment line whose inline comment contains @pubsub.
  // The regex handles:
  //   width = 10; // [0:100] @pubsub
  //   pos = [0, 0]; // [0:100, 0:100] @pubsub Some description
  // It intentionally does NOT strip group comment lines (/* [...] */).
  const pubsubLineRe =
    /^[ \t]*\$?[a-zA-Z_][a-zA-Z0-9_]*\s*=\s*[^;]+;[ \t]*\/\/[^\n]*@pubsub\b[^\n]*$/gm;
  return code.replace(pubsubLineRe, '');
}

// ---------------------------------------------------------------------------
// Syntax error checking
// ---------------------------------------------------------------------------

/**
 * Returns true when the given SCAD source has at least one syntax error
 * according to the openscad-parser.
 *
 * Preprocesses custom modifiers first so that `%`, `~`, and JSON includes are
 * not incorrectly flagged as parse errors.
 *
 * Returns false when the parser itself throws (unexpected internal error)
 * so that a parser crash never blocks compilation.
 *
 * @param code         Raw SCAD source.
 * @param projectFiles Optional uploaded project files for JSON include resolution.
 */
export function hasSyntaxError(
  code: string,
  projectFiles?: JsonProjectFile[],
): boolean {
  try {
    const preprocessed = preprocessScadModifiers(code, projectFiles);
    const codeFile = new CodeFile('input.scad', preprocessed);
    const [, errorCollector] = ParsingHelper.parseFile(codeFile);
    return errorCollector.errors.length > 0;
  } catch {
    return false;
  }
}
